/* @flow */

export const handleCallback = (resolve: (any) => void, reject: (any) => void, callback: (any) => any, value: any) => {
  try {
    resolve(callback(value))
  } catch (e) {
    reject(e)
  }
}

/**
 * Now we can cancel the promise and a function is called in the promise when we do.
 * Check out the samples for more details...
 *
 * @param {function} executor function given resolve, reject, onCancel
 * @example
 * const p1 = new CancelablePromise((resolve, reject, onCancel) => {
 *   onCancel(() => {
 *     // promise is cancelled
 *   })
 * })
 */
class CancelablePromise {
  _onCancel: (any) => any
  _canceled: boolean
  _status: string
  _promise: CancelablePromise | Promise<any>
  _resolve: (any) => any
  _reject: (any) => any

  /**
   * resolve promise once all promises/values are resolved. if one is rejected/canceled, it will be rejected
   * @param  {Array} iterable    Array of promises/values
   * @return {CancelablePromise} promise containing the result of promises as array
   * @example
   * const p1 = new CancelablePromise((resolve, reject) => {
   *   ...resolve(1)
   * })
   * const p2 = new CancelablePromise((resolve, reject) => {
   *   ...resolve(2)
   * })
   * const p3 = new CancelablePromise((resolve, reject) => {
   *   ...resolve(3)
   * })
   * p2.cancel()
   * CancelablePromise.all([p1, p2, p3, 4])
   * // promise - rejected
   */
  static all(iterable: Array<mixed>) {
    return new CancelablePromise((resolve, reject, onCancel) => {
      const length = iterable.length
      if (length == 0) return resolve([])
      let count = 0
      // $FlowFixMe: create array of lenght length
      const results = Array.apply(null, { length: length })
      const checkResult = (index) => (result) => {
        count += 1
        results[index] = result
        if (count == length) {
          resolve(results)
        }
      }
      onCancel(() => {
        iterable.forEach((promise) => {
          if (promise instanceof CancelablePromise) promise.cancel()
        })
      })
      iterable.forEach((promise, index) => {
        if (promise && typeof promise.then == 'function') {
          // $FlowFixMe: in case of CancelablePromise it takes a third param
          promise.then(checkResult(index), reject, reject)
        } else {
          checkResult(index)(promise)
        }
      })
    })
  }

  /**
   * Resolves one or more promises that can be resolved from the iterable Array parameter
   * @param  {Array} iterable Array of promises/values
   * @return {CancelablePromise} promise containing the result of promises as an array
   */
  static some(iterable: Array<mixed>) {
    return new CancelablePromise((resolve, _reject, onCancel) => {
      const length = iterable.length
      if (length == 0) return resolve([])
      let count = 0
      // $FlowFixMe: create array of length length
      const results = Array.apply(null, { length: length })
      const checkResult = (index) => (result) => {
        count += 1
        results[index] = result
        if (count == length) {
          resolve(results)
        }
      }
      const checkResultNull = (index) => () => {
        checkResult(index)(null)
      }
      onCancel(() => {
        iterable.forEach((promise) => {
          if (promise instanceof CancelablePromise) promise.cancel()
        })
      })
      iterable.forEach((promise, index) => {
        if (promise && typeof promise.then == 'function') {
          // $FlowFixMe: in case of CancelablePromise it takes a third param
          promise.then(checkResult(index), checkResultNull(index), checkResultNull(index))
        } else {
          checkResult(index)(promise)
        }
      })
    })
  }

  /**
   * resolve promise once all promises/values are resolved/canceled. if one is rejected, all will be rejected
   * @param  {Array} iterable    Array of promises/values
   * @return {CancelablePromise} promise containing the results of promises as array
   * @example
   * const p1 = new CancelablePromise((resolve, reject) => {
   *   ...resolve(1)
   * })
   * const p2 = new CancelablePromise((resolve, reject) => {
   *   ...resolve(2)
   * })
   * const p3 = new CancelablePromise((resolve, reject) => {
   *   ...resolve(3)
   * })
   * p2.cancel()
   * CancelablePromise.allCancelable([p1, p2, p3, 4])
   * // promise - resolved [1, 3, 4]
   */
  static allCancelable(iterable: Array<CancelablePromise>) {
    return new CancelablePromise((resolve, reject) => {
      const length = iterable.length
      if (length == 0) return resolve([])
      let count = 0
      // $FlowFixMe: this is to generate an array of length length
      const results = Array.apply(null, { length: length })
      const checkResult = (promise, index) => (result) => {
        count += 1
        if (promise && typeof promise.then == 'function') {
          results[index] = promise._canceled ? null : result
        } else {
          results[index] = result
        }

        if (count == length) {
          resolve(results)
        }
      }
      iterable.forEach((promise, index) => {
        if (promise && typeof promise.then == 'function') {
          promise.then(checkResult(promise, index), reject, checkResult(promise, index))
        } else {
          checkResult(promise, index)(promise)
        }
      })
    })
  }

  /**
   * resolve/reject/cancel when the first promise is finish
   * @param  {Array} iterable    array of promises
   * @return {CancelablePromise} first promise that was finish
   * @example
   * const p1 = new CancelablePromise((resolve, reject) => {
   *   ...resolve(1)
   * })
   * const p2 = new CancelablePromise((resolve, reject) => {})
   * const p3 = new CancelablePromise((resolve, reject) => {})
   * CancelablePromise.race([p1, p2, p3])
   * // promise - resolved 1
   */
  static race(iterable: Array<Promise<any> | CancelablePromise>) {
    const promise = new CancelablePromise((resolve, reject) => {
      let done = false
      iterable.forEach((p) => {
        if (p && typeof p.then == 'function') {
          // $FlowFixMe: in case of CancelablePromise it takes a third param
          p.then(
            (result) => {
              if (!done) {
                done = true
                resolve(result)
              }
            },
            (error) => {
              if (!done) {
                done = true
                reject(error)
              }
            },
            () => {
              if (!done) {
                done = true
                promise.cancel()
              }
            }
          )
        }
      })
    })
    return promise
  }

  /**
   * creates new promise in rejected state
   * @param  {string|Error} value set an reason why the promise was rejected
   * @return {CancelablePromise}  new promise to chain
   */
  static reject(value: any) {
    return new CancelablePromise((resolve, reject) => {
      Promise.reject(value).then(resolve, reject)
    })
  }

  /**
   * creates new promise in resolved state
   * @param  {*} value           any value the promise should return
   * @return {CancelablePromise} new promise to chain
   */
  static resolve(value: any) {
    return new CancelablePromise((resolve, reject) => {
      Promise.resolve(value).then(resolve, reject)
    })
  }

  constructor(executor: (any, any, any) => any) {
    this._canceled = false
    this._status = 'running'
    this._promise = new Promise((resolve, reject) => {
      this._resolve = resolve
      this._reject = reject
      executor(resolve, reject, (callback) => {
        this._onCancel = callback
      })
    })
  }

  /**
   * function to regiester if promise was resolved, rejected or canceled
   * @param  {function} success  callback if promise was resolved
   * @param  {function} error    callback if promise was rejected
   * @param  {function} cancel   callack if promise was cancelled
   * @return {CancelablePromise} new promise to chain
   */
  then(success?: (any) => any, error?: (any) => any, cancel?: (any) => any) {
    const promise = new this.constructor((resolve, reject, onCancel) => {
      onCancel(this._onCancel)
      this._promise.then(
        (result) => {
          if (this._status == 'running') this._status = 'resolved'
          promise._status = this._status
          promise._canceled = this._canceled
          if (cancel && this._canceled) {
            handleCallback(resolve, reject, cancel, result)
          } else if (success && !this._canceled) {
            handleCallback(resolve, reject, success, result)
          } else {
            resolve(result)
          }
        },
        (result) => {
          if (this._status == 'running') this._status = 'rejected'
          promise._status = this._status
          promise._canceled = this._canceled
          if (cancel && this._canceled) {
            handleCallback(resolve, reject, cancel, result)
          } else if (error && !this._canceled) {
            handleCallback(resolve, reject, error, result)
          } else {
            reject(result)
          }
        }
      )
    })
    return promise
  }

  /**
   * called when the promise was rejected or aborted
   * @param  {function} error    callback if rejected or aborted
   * @return {CancelablePromise} new promise to chain
   */
  catch(error: (any) => any) {
    return this.then(undefined, error, undefined)
  }

  /**
   * called when promise is done, resolved, rejected or cancelled
   * @param  {function} func     callback if finish
   * @return {CancelablePromise} new promise to chain
   */
  finally(func: (any) => any) {
    return this.then(func, func, func)
  }

  /**
   * called when promise is resolved or rejected
   * @param  {function} func     callback if finish
   * @return {CancelablePromise} new promise to chain
   */
  finish(func: (any) => any) {
    return this.then(func, func)
  }

  /**
   * get if the promise is getting cancelled
   * @param  {function} func     callback if cancelled
   * @return {CancelablePromise} new promise to chain
   */
  cancelled(func: (any) => any) {
    return this.then(undefined, undefined, func)
  }

  /**
   * cancel current promise
   * @return {CancelablePromise} returns current promise
   * @example
   * const promise = new CancelablePromise((resolve, reject, onCancel) => {
   *   onCancel(() => {
   *     // cancel something here
   *   })
   * })
   * promise.cancel()
   * // promise - cancelled
   * promise.cancelled(() => {})
   * promise.then(null, null, () => {})
   */
  cancel() {
    this._canceled = true
    if (this._status == 'running') {
      this._status = 'cancelled'
      this._resolve()
      if (typeof this._onCancel == 'function') this._onCancel()
    }
    return this
  }

  /**
   * abort a promise from outside, which will reject it
   * @param  {string|Error} reason message why the promise was canceled, given by catching the promise
   * @return {CancelablePromise}   current promise
   * @example
   * const promise = new CancelablePromise((resolve, reject, onCancel) => {
   *   onCancel(() => {
   *     // cancel something here
   *   })
   * })
   * promise.abort()
   * // promise - rejected
   * promise.catch(() => {})
   * promise.then(null, () => {})
   */
  abort(reason: string | Error) {
    if (this._status == 'running') {
      this._status = 'aborted'
      this._reject(reason)
      if (typeof this._onCancel == 'function') this._onCancel()
    }
    return this
  }
}

export default CancelablePromise
